import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  Paper,
  Tooltip,
  Alert,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ViewListIcon from '@mui/icons-material/ViewList';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  ALL_MENU_CATALOG,
  resolveMenuConfig,
  MENU_PRESETS,
} from '../../../shared/constants/menuCatalog';
import { useUpdateCurrentCompanyMutation } from '../../../store/api/companyApi';

export function MenuOrderCustomizer({ company }) {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [updateCompany, { isLoading: isSaving }] = useUpdateCurrentCompanyMutation();

  // Initialize items from company.menuOrder or fallback to default
  useEffect(() => {
    if (company) {
      const resolved = resolveMenuConfig(company.menuOrder);
      setItems(resolved);
    }
  }, [company]);

  // Reordering helpers
  const moveItem = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= items.length || fromIndex === toIndex) return;
    setItems((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, moved);
      return copy;
    });
    setSaveSuccess(false);
  };

  const moveToTop = (index) => {
    if (index === 0) return;
    moveItem(index, 0);
  };

  const moveToBottom = (index) => {
    if (index === items.length - 1) return;
    moveItem(index, items.length - 1);
  };

  const toggleVisibility = (id) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, visible: !item.visible } : item))
    );
    setSaveSuccess(false);
  };

  const applyPreset = (presetOrder) => {
    const orderMap = new Map();
    presetOrder.forEach((id, idx) => orderMap.set(id, idx));

    const sorted = [...ALL_MENU_CATALOG].sort((a, b) => {
      const aIdx = orderMap.has(a.id) ? orderMap.get(a.id) : 999;
      const bIdx = orderMap.has(b.id) ? orderMap.get(b.id) : 999;
      return aIdx - bIdx;
    });

    setItems(sorted.map((item) => ({ ...item, visible: true })));
    setSaveSuccess(false);
  };

  const resetToDefault = () => {
    applyPreset(MENU_PRESETS[0].order);
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = 'move';
    // Transparent or custom drag preview
    if (e.target) {
      e.target.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e) => {
    if (e.target) {
      e.target.style.opacity = '1';
    }
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === index) return;
    setDragOverIdx(index);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIdx !== null && draggedIdx !== targetIndex) {
      moveItem(draggedIdx, targetIndex);
    }
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  // Save handler
  const handleSave = async () => {
    try {
      setSaveError(null);
      // Construct clean serializable payload: array of { id, path, visible }
      const payload = items.map((item) => ({
        id: item.id,
        path: item.path,
        visible: item.visible !== false,
      }));

      await updateCompany({ menuOrder: payload }).unwrap();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      console.error('Failed to save menu order:', err);
      setSaveError(err?.data?.message || err?.message || 'Failed to save menu order');
    }
  };

  // Filtered list for search
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase().trim();
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        item.path.toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const visibleCount = items.filter((i) => i.visible !== false).length;
  const hiddenCount = items.length - visibleCount;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ── Header & Action Banner ──────────────────────────────────── */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <ViewListIcon sx={{ color: '#0ea5e9' }} /> Tenant Menu & Navigation Order
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                Customize the top navigation and mobile drawer order specifically for <strong>{company?.name || 'this company'}</strong>. Drag items or use arrows to rearrange.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Chip
                  size="small"
                  label={`${visibleCount} Visible`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600, bgcolor: 'rgba(14, 165, 233, 0.08)' }}
                />
                {hiddenCount > 0 && (
                  <Chip
                    size="small"
                    label={`${hiddenCount} Hidden`}
                    sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={resetToDefault}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Reset Default
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: '#0ea5e9',
                  '&:hover': { bgcolor: '#0284c7' },
                  boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
                }}
              >
                {isSaving ? 'Saving...' : 'Save Menu Order'}
              </Button>
            </Box>
          </Box>

          {saveSuccess && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2, borderRadius: 2 }}>
              Menu order saved successfully! The navigation bar has updated to your custom order.
            </Alert>
          )}

          {saveError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {saveError}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ── Quick Preset Selector ─────────────────────────────────────── */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #e2e8f0',
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: '#f59e0b', fontSize: 20 }} /> Quick Presets
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Click any preset to automatically re-arrange menu options to best fit your business model:
          </Typography>
          <Grid container spacing={2}>
            {MENU_PRESETS.map((preset) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={preset.id}>
                <Paper
                  elevation={0}
                  onClick={() => applyPreset(preset.order)}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    bgcolor: '#f8fafc',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:hover': {
                      borderColor: '#0ea5e9',
                      bgcolor: '#f0f9ff',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(14, 165, 233, 0.15)',
                    },
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                      {preset.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.4 }}>
                      {preset.description}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ mt: 1.5, alignSelf: 'flex-start', p: 0, textTransform: 'none', fontWeight: 700, color: '#0284c7' }}
                  >
                    Apply Layout →
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* ── Live Navigation Preview Bar ───────────────────────────────── */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #e2e8f0',
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
              👀 Live Top Navigation Preview
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              How users in this company will see the navigation bar
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              overflowX: 'auto',
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 4 },
            }}
          >
            {items
              .filter((item) => item.visible !== false)
              .map((item, index) => {
                const IconComp = item.icon;
                const isFirst = index === 0;
                return (
                  <Chip
                    key={item.id}
                    icon={IconComp ? <IconComp sx={{ fontSize: '18px !important', color: isFirst ? 'white !important' : '#64748b !important' }} /> : null}
                    label={item.label}
                    size="small"
                    sx={{
                      height: 32,
                      px: 0.5,
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      borderRadius: 2,
                      bgcolor: isFirst ? '#0ea5e9' : '#ffffff',
                      color: isFirst ? '#ffffff' : '#334155',
                      border: isFirst ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                      boxShadow: isFirst ? '0 2px 6px rgba(14, 165, 233, 0.3)' : '0 1px 2px rgba(0,0,0,0.03)',
                      flexShrink: 0,
                    }}
                  />
                );
              })}
          </Box>
        </CardContent>
      </Card>

      {/* ── Reorderable Menu Items List ───────────────────────────────── */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #e2e8f0',
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                All Navigation Menu Options
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Drag the handles or click the arrow buttons to change order. Toggle switches to hide/show options.
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder="Search menu options..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: { xs: '100%', sm: 260 } }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredItems.map((item, filteredIndex) => {
              const actualIndex = items.findIndex((i) => i.id === item.id);
              const IconComp = item.icon;
              const isDragging = draggedIdx === actualIndex;
              const isDragOver = dragOverIdx === actualIndex;

              return (
                <Paper
                  key={item.id}
                  draggable={!searchQuery} // disable drag during search filter for order safety
                  onDragStart={(e) => handleDragStart(e, actualIndex)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, actualIndex)}
                  onDrop={(e) => handleDrop(e, actualIndex)}
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isDragOver ? '#0ea5e9' : item.visible ? '#e2e8f0' : '#f1f5f9',
                    bgcolor: isDragOver ? '#f0f9ff' : item.visible ? '#ffffff' : '#f8fafc',
                    opacity: isDragging ? 0.4 : item.visible ? 1 : 0.65,
                    transition: 'all 0.15s ease',
                    boxShadow: isDragOver ? '0 0 0 2px rgba(14, 165, 233, 0.3)' : 'none',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      bgcolor: item.visible ? '#f8fafc' : '#f1f5f9',
                    },
                  }}
                >
                  {/* Left: Drag Handle, Icon, Name & Category */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    {/* Drag Handle */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#94a3b8',
                        cursor: searchQuery ? 'default' : 'grab',
                        '&:active': { cursor: 'grabbing' },
                        p: 0.5,
                      }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>

                    {/* Order Number Badge */}
                    <Chip
                      size="small"
                      label={`#${actualIndex + 1}`}
                      sx={{
                        width: 38,
                        height: 24,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        bgcolor: actualIndex < 3 ? '#e0f2fe' : '#f1f5f9',
                        color: actualIndex < 3 ? '#0284c7' : '#64748b',
                      }}
                    />

                    {/* Module Icon */}
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        bgcolor: item.visible ? '#f0fdf4' : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: item.visible ? '#16a34a' : '#94a3b8',
                        border: '1px solid',
                        borderColor: item.visible ? '#bbf7d0' : '#e2e8f0',
                      }}
                    >
                      {IconComp ? <IconComp fontSize="small" /> : null}
                    </Box>

                    {/* Title and Module */}
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: item.visible ? '#0f172a' : '#64748b',
                            textDecoration: item.visible ? 'none' : 'line-through',
                          }}
                        >
                          {item.label}
                        </Typography>
                        {item.isSuper && (
                          <Chip size="small" label="SuperAdmin" color="warning" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                        {item.category} • <span style={{ fontFamily: 'monospace' }}>{item.path}</span>
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right: Actions (Move buttons + Visibility Toggle) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {/* Move to Top */}
                    <Tooltip title="Move to Top">
                      <span>
                        <IconButton
                          size="small"
                          disabled={actualIndex === 0 || !!searchQuery}
                          onClick={() => moveToTop(actualIndex)}
                          sx={{ color: '#64748b', p: 0.75 }}
                        >
                          <VerticalAlignTopIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Move Up */}
                    <Tooltip title="Move Up">
                      <span>
                        <IconButton
                          size="small"
                          disabled={actualIndex === 0 || !!searchQuery}
                          onClick={() => moveItem(actualIndex, actualIndex - 1)}
                          sx={{ color: '#64748b', p: 0.75 }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Move Down */}
                    <Tooltip title="Move Down">
                      <span>
                        <IconButton
                          size="small"
                          disabled={actualIndex === items.length - 1 || !!searchQuery}
                          onClick={() => moveItem(actualIndex, actualIndex + 1)}
                          sx={{ color: '#64748b', p: 0.75 }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Move to Bottom */}
                    <Tooltip title="Move to Bottom">
                      <span>
                        <IconButton
                          size="small"
                          disabled={actualIndex === items.length - 1 || !!searchQuery}
                          onClick={() => moveToBottom(actualIndex)}
                          sx={{ color: '#64748b', p: 0.75 }}
                        >
                          <VerticalAlignBottomIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Visibility Switch */}
                    <Tooltip title={item.visible ? 'Visible in menu (Click to Hide)' : 'Hidden from menu (Click to Show)'}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={item.visible !== false}
                            onChange={() => toggleVisibility(item.id)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ fontWeight: 600, color: item.visible ? '#0284c7' : '#94a3b8', display: { xs: 'none', sm: 'inline' } }}>
                            {item.visible ? 'Visible' : 'Hidden'}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Tooltip>
                  </Box>
                </Paper>
              );
            })}
          </Box>

          {/* Bottom Save Bar */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RestartAltIcon />}
              onClick={resetToDefault}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Reset Default
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: '#0ea5e9',
                '&:hover': { bgcolor: '#0284c7' },
                boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
              }}
            >
              {isSaving ? 'Saving...' : 'Save Menu Order'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
